import {
  Box,
  Card,
  CardSection,
  Checkbox,
  Flex,
  Group,
  MultiSelect,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { IconChevronDown, IconSearch } from '@tabler/icons-react'
import classes from './PermissionSets.module.css'
import { useState } from 'react'
import cn from 'classnames'
import { PERMISSION_SETS_RECORDS } from '@constants/permissionSets/records'
import { PERMISSION_SETS_REFERRALS } from '@constants/permissionSets/referrals'
import { PERMISSION_SETS_STAFF } from '@constants/permissionSets/staff'
import { PERMISSION_SETS_PROFILE_CONTENT } from '@constants/permissionSets/profileContent'
import { PERMISSION_SETS_TAGS } from '@constants/permissionSets/tags'
import { PERMISSION_SETS_TASKS } from '@constants/permissionSets/tasks'
import { PERMISSION_SETS_CALLS } from '@constants/permissionSets/calls'
import { PERMISSION_SETS_NOTES_INCIDENTS } from '@constants/permissionSets/notesIncidents'
import { PERMISSION_SETS_FILES } from '@constants/permissionSets/files'
import { PERMISSION_SETS_ACTIVITY } from '@constants/permissionSets/activity'
import { PERMISSION_SETS_STAGES } from '@constants/permissionSets/stages'
import { PERMISSION_SETS_ACCOUNT_SETTINGS } from '@constants/permissionSets/accountSettings'

export const PERMISSION_SETS = [
  PERMISSION_SETS_RECORDS,
  PERMISSION_SETS_REFERRALS,
  PERMISSION_SETS_STAFF,
  PERMISSION_SETS_PROFILE_CONTENT,
  PERMISSION_SETS_TAGS,
  PERMISSION_SETS_TASKS,
  PERMISSION_SETS_CALLS,
  PERMISSION_SETS_NOTES_INCIDENTS,
  PERMISSION_SETS_FILES,
  PERMISSION_SETS_ACTIVITY,
  PERMISSION_SETS_STAGES,
  PERMISSION_SETS_ACCOUNT_SETTINGS,
]

export const PermissionSetsDetails = () => {
  const [activeSet, setActiveSet] = useState<string>(PERMISSION_SETS[0].id)
  const [activeSetData, setActiveSetData] = useState<any>(
    PERMISSION_SETS.find((curr) => curr.id === activeSet),
  )

  const handleSetClick = (id: string) => {
    console.log(PERMISSION_SETS.find((curr) => curr.id === id))
    setActiveSet(id)
    setActiveSetData(PERMISSION_SETS.find((curr) => curr.id === id))
  }

  return (
    <Stack p="md" w={750} gap="xl">
      <Title order={4}>Edit Permission Set</Title>
      <TextInput
        label="Permission set name"
        size="md"
        defaultValue="Administration"
        withAsterisk
      />
      <Stack gap="xs">
        <Title order={4}>Customize this set of permissions</Title>
        <Text>
          Customize the permissions below to tailor the way staff with this
          permission set use BlueCard.
        </Text>
        <Card withBorder>
          <Stack gap="xs">
            <Checkbox
              label={
                <Text fw="bold" c="dark">
                  Full access
                </Text>
              }
              checked
            />
            <Text ml="xl">
              This enables all permissions except managing subscriptions to
              BlueCard services and add-ons, representatives, permissions sets,
              all locations, teams, and records, and closing the account, which
              only account representatives can do.
            </Text>
          </Stack>
        </Card>
      </Stack>
      <Stack>
        <TextInput
          leftSection={<IconSearch size={18} />}
          leftSectionPointerEvents="none"
          size="md"
          placeholder="Search permissions"
        />

        <Flex gap="md">
          <Stack w={250} gap={5} style={{ flexShrink: 0 }}>
            {PERMISSION_SETS.map((curr, i) => (
              <Flex
                key={`permission-sets-left-${i}`}
                className={cn(classes.PermissionSets__item, {
                  [classes.PermissionSets__itemActive]: activeSet === curr.id,
                })}
                onClick={() => handleSetClick(curr.id)}
                p="xs"
                align="center"
                gap="xs"
              >
                <ThemeIcon variant="subtle">
                  <curr.icon size={22} />
                </ThemeIcon>
                <Text fw="bold" size="sm" style={{ flexGrow: 1 }}>
                  {curr.id}
                </Text>
                <Text size="xs" fw="bold" c="gray.7">
                  On
                </Text>
              </Flex>
            ))}
          </Stack>

          {!activeSetData?.sections ? (
            <Card style={{ flexGrow: 1 }} withBorder>
              <CardSection p="md">
                <Checkbox
                  size="xs"
                  label={
                    <Title order={5} mt={-3}>
                      {activeSetData?.id}
                    </Title>
                  }
                  description={activeSetData?.description ?? ''}
                  checked
                />
              </CardSection>
            </Card>
          ) : (
            <Card style={{ flexGrow: 1 }} withBorder>
              <CardSection p="md" withBorder>
                <Checkbox
                  size="xs"
                  label={
                    <Title order={5} mt={-3}>
                      {activeSetData?.id}
                    </Title>
                  }
                  description={activeSetData?.description ?? ''}
                  checked
                />
              </CardSection>
              <CardSection p="md">
                <Stack gap="xs">
                  {activeSetData?.sections &&
                    activeSetData?.sections.map((curr: any, i: number) => (
                      <Stack gap={2} key={`set-top-${i}`}>
                        <Text
                          c="gray.6"
                          fw="bold"
                          tt="uppercase"
                          size="sm"
                          mb="xs"
                        >
                          {curr.title}
                        </Text>
                        {curr.permissions &&
                          curr.permissions.map((set: any, x: number) => (
                            <>
                              {set.type === 'checkbox' && (
                                <>
                                  {set.children ? (
                                    <Stack key={`withchild-${x}`} gap={5}>
                                      <Group gap="xs" align="flex-start">
                                        <IconChevronDown size={18} />
                                        <Checkbox
                                          size="xs"
                                          label={
                                            <Text size="sm" mt={-1}>
                                              {set.label}
                                            </Text>
                                          }
                                          description={
                                            (
                                              <Text size="sm">
                                                {set?.description}
                                              </Text>
                                            ) ?? ''
                                          }
                                          checked
                                        />
                                      </Group>
                                      <Box ml="xl" pl="xs">
                                        {set.children.map(
                                          (child: any, y: number) => (
                                            <>
                                              {child.type === 'checkbox' && (
                                                <Checkbox
                                                  key={`child-check-${y}`}
                                                  size="xs"
                                                  label={
                                                    <Text mt={-1} size="sm">
                                                      {child.label}
                                                    </Text>
                                                  }
                                                  description={
                                                    (
                                                      <Text size="sm">
                                                        {set?.description}
                                                      </Text>
                                                    ) ?? ''
                                                  }
                                                  checked
                                                />
                                              )}
                                              {child.type === 'multselect' && (
                                                <MultiSelect
                                                  key={`child-check-${y}`}
                                                  label={child.label}
                                                  data={child.data}
                                                  placeholder="Select one or more"
                                                  withAsterisk={
                                                    child.withAsterisk
                                                  }
                                                />
                                              )}
                                            </>
                                          ),
                                        )}
                                      </Box>
                                    </Stack>
                                  ) : (
                                    <Checkbox
                                      size="xs"
                                      label={
                                        <Text mt={-1} size="sm">
                                          {set.label}
                                        </Text>
                                      }
                                      description={
                                        (
                                          <Text size="sm">
                                            {set?.description}
                                          </Text>
                                        ) ?? ''
                                      }
                                      checked
                                    />
                                  )}
                                </>
                              )}
                              {set.type === 'multiselect' && (
                                <MultiSelect
                                  size="md"
                                  pl="xl"
                                  ml={-3}
                                  mt="xs"
                                  mb="sm"
                                  label={set.label}
                                  data={set.data}
                                  placeholder="Select one or more"
                                  withAsterisk={set.withAsterisk}
                                />
                              )}
                            </>
                          ))}
                      </Stack>
                    ))}
                </Stack>
              </CardSection>
            </Card>
          )}
        </Flex>
      </Stack>
    </Stack>
  )
}
